import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Building2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Page, PageContent } from '@bliks-io/ui';
import { HubspotCompanyCard } from './components/HubspotCompanyCard';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import { useListAccounts } from '@/pages/admin/api/queries/useAccounts';
import { useUpdateAccount } from '@/pages/admin/api/mutations/useAccounts';
import { useRefreshAccountCrm } from '@/pages/admin/api/mutations/useRefreshAccountCrm';
import { AssignCompanyDialog } from './components/AssignCompanyDialog';
import { AccountGroupCard } from './components/AccountGroupCard';
import { CreateTenantDialog } from '@/pages/admin/tenants/CreateTenantDialog';
import { TenantsTable } from '@/pages/admin/tenants/TenantsTable';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import { useHubspotCompanyOverview } from '@/pages/admin/api/queries/useHubspotCompanyOverview';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * One customer: who they are and which workspaces they run.
 *
 * The company's name and domain from HubSpot, which is what that record is
 * for; the workspaces from the tenant list.
 */
export function AccountDetailPage() {
  const { t } = useTranslation();
  const { accountId } = useParams({ from: '/admin/accounts/$accountId/' });

  const [assignOpen, setAssignOpen] = useState(false);
  const [addTenantOpen, setAddTenantOpen] = useState(false);
  const updateAccount = useUpdateAccount();
  const crmRefresh = useRefreshAccountCrm(accountId);

  /**
   * Der Abgleich sagt, was er getan hat — auch wenn das „nichts" ist.
   *
   * Drei Antworten, und sie sind verschieden: gar nicht gelesen (keine Firma
   * oder das CRM schweigt), gelesen und nichts Neues, gelesen und etwas
   * geändert. Ein Knopf, der wortlos nichts tut, sieht aus wie ein kaputter.
   */
  const refreshCrm = () =>
    crmRefresh.mutate(undefined, {
      onSuccess: (result) => {
        if (!result.read) toast.warning(t('accountAdmin.company.refreshUnread'));
        else if (result.changed) toast.success(t('accountAdmin.company.refreshChanged'));
        else toast.info(t('accountAdmin.company.refreshUnchanged'));

        // Zwei Personen mit demselben Etikett: wir nehmen die erste, und
        // welche das ist, entscheidet HubSpots Reihenfolge. Wer nachsieht,
        // soll erfahren, dass hier gerade der Zufall entscheidet.
        for (const role of result.ambiguousRoles) {
          toast.warning(t(`accountAdmin.company.ambiguous.${role}`));
        }
      },
      onError: (error) => toast.error(translateErrorMessage(error as unknown as ApiError, t))
    });

  const { data: accountsData, isLoading: accountsLoading } = useListAccounts();
  const accounts = accountsData?.accounts ?? [];
  const account = accounts.find((a) => a._id === accountId);
  const children = accounts.filter((a) => a.parent?._id === accountId);

  const save = (data: Parameters<typeof updateAccount.mutate>[0]['data']) => {
    updateAccount.mutate(
      { accountId, data },
      {
        onSuccess: () => {
          setAssignOpen(false);
          toast.success(t('accountAdmin.saved'));
        },
        // The backend refuses a taken company and a cycle by name — surfacing
        // its reason beats a generic failure the admin cannot act on.
        onError: (error) => toast.error(translateErrorMessage(error as unknown as ApiError, t))
      }
    );
  };

  // HubSpot's to answer: the company's name and domain are the CRM's record
  // of who this customer is.
  // The list row carries the company id derived from the account's refs.
  const hubspotCompanyId = account?.hubspotCompanyId;
  const { data: overview } = useHubspotCompanyOverview(hubspotCompanyId ?? '');
  const { data: tenantsData } = useListTenants();
  const tenants = tenantsData?.tenants.filter((tenant) => tenant.accountId === accountId) ?? [];

  useDocumentTitle(
    account?.name ? `${t('accountAdmin.title')}: ${account.name}` : t('accountAdmin.title')
  );

  if (!account && !accountsLoading) {
    return (
      <Page>
        <PageContent>
          <p className="text-muted-foreground">{t('accountAdmin.notFound')}</p>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageContent>
        {/* Zurueck, weil diese Seite immer aus einer Liste heraus geoeffnet
            wird — der Browser-Knopf ist kein Ersatz fuer einen Weg, den die
            Seite selbst anbietet. */}
        <Link
          to="/admin/accounts"
          className="text-muted-foreground mb-4 inline-flex items-center text-sm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('accountAdmin.backToAccounts')}
        </Link>

        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {account?.name ?? t('accountAdmin.unnamed')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {overview?.domain ?? account?.domain ?? hubspotCompanyId ?? ''}
            </p>
            {account?.parent && (
              <p className="text-sm text-muted-foreground mt-1">
                {t('accountAdmin.partOf')}{' '}
                <Link
                  to="/admin/accounts/$accountId"
                  params={{ accountId: account.parent._id }}
                  className="text-primary hover:underline"
                >
                  {account.parent.name ?? t('accountAdmin.unnamed')}
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className="mb-8">
          <HubspotCompanyCard
            companyId={hubspotCompanyId}
            overview={overview}
            primaryContact={account?.primaryContact}
            billingContact={account?.billingContact}
            masterData={account}
            onEdit={() => setAssignOpen(true)}
            onRefresh={refreshCrm}
            isRefreshing={crmRefresh.isPending}
          />
        </div>

        {account && (
          <div className="mb-8">
            <AccountGroupCard
              account={account}
              candidates={accounts}
              childAccounts={children}
              onSetParent={(parentAccountId) => save({ parentAccountId })}
              isSaving={updateAccount.isPending}
            />
          </div>
        )}

        {/* A customer may run several workspaces: a consultancy runs a partner
            one and its own. */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {t('accountAdmin.tenants')}
            </h2>
            <Button variant="outline" size="small" onClick={() => setAddTenantOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('accountAdmin.addTenant')}
            </Button>
          </div>
          <TenantsTable tenants={tenants} tableId="accountTenants" keepsLayoutWhenEmpty />
        </section>

        {account && (
          <CreateTenantDialog
            open={addTenantOpen}
            onOpenChange={setAddTenantOpen}
            accountId={account._id}
          />
        )}

        {account && (
          <AssignCompanyDialog
            open={assignOpen}
            onOpenChange={setAssignOpen}
            accountId={account._id}
            onAssign={(hubspotCompanyId) => save({ hubspotCompanyId })}
            isAssigning={updateAccount.isPending}
          />
        )}
      </PageContent>
    </Page>
  );
}
